import { type ReactNode } from 'react';
import {
  AlertStatusIcon,
  CheckStatusIcon,
  CloseIcon,
  InfoStatusIcon,
} from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger';

export interface BannerProps {
  readonly tone?: BannerTone;
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
  readonly onDismiss?: () => void;
  readonly className?: string;
}

const TONE: Record<BannerTone, { box: string; iconClass: string; Icon: typeof InfoStatusIcon }> = {
  info: { box: 'bg-info/10 border-info/30', iconClass: 'text-info', Icon: InfoStatusIcon },
  success: {
    box: 'bg-success/10 border-success/30',
    iconClass: 'text-success',
    Icon: CheckStatusIcon,
  },
  warning: {
    box: 'bg-warning/10 border-warning/40',
    iconClass: 'text-warning',
    Icon: AlertStatusIcon,
  },
  danger: { box: 'bg-danger/10 border-danger/40', iconClass: 'text-danger', Icon: AlertStatusIcon },
};

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  onDismiss,
  className,
}: BannerProps) {
  const { box, iconClass, Icon } = TONE[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 p-3 rounded-[10px] border', box, className)}
    >
      <div className={cn('shrink-0 mt-0.5', iconClass)}>
        <Icon size={18} color="currentColor" />
      </div>
      <div className="flex-1 min-w-0">
        {title ? <div className="text-[13.5px] font-semibold text-ink">{title}</div> : null}
        {children ? <div className="text-[12.5px] text-ink-2 leading-snug">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 -m-1 size-7 inline-flex items-center justify-center rounded-md text-ink-2 hover:bg-black/5 dark:hover:bg-white/5 focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40"
        >
          <CloseIcon size={14} color="currentColor" />
        </button>
      ) : null}
    </div>
  );
}
